import { z } from "zod";
import { dataSourceLinkSelectionSchema } from "../data-source-link-selection";

/**
 * Base config schema shared by all blob-based DataSource Adapter steps.
 * Covers artifact storage (where chunks are written) and DataSource Link
 * resolution (static pick or matching-schema fan-out).
 *
 * Step-specific schemas extend this with their own parsing/mapping options:
 *   configSchema: dataSourceAdapterBaseConfigSchema.extend({ ... })
 */
export const dataSourceAdapterBaseConfigSchema = z
  .object({
    artifactLocationUri: z
      .string()
      .optional()
      .describe(
        "Blob Storage URI of the folder where intermediate artifacts are written",
      ),
    artifactStorageProfile: z
      .enum(["default", "ship_and_debit"])
      .optional()
      .describe("Storage profile used to access the artifact location"),
    chunkSize: z
      .number()
      .int()
      .min(-1)
      .optional()
      .describe(
        "Maximum number of rows per output chunk. -1 means no chunking (write all rows to a single file).",
      ),
    matchingTableTemplate: z
      .string()
      .optional()
      .describe(
        "Table-name template used with the 'matching_schema' strategy to resolve a " +
          "single target DataSource Link from the input blob URI. Supports {DIR}, {FILE}, " +
          "{DIR:n}, {DIR:-n}, {FILE:n}, {FILE:-n} substitutions. The resolved value is " +
          "matched case-insensitively against the foreignTableName of the schema's links. " +
          "An adapter run processes one source blob, so it must resolve to exactly one " +
          "link (each output chunk pairs with that link); the run fails on zero or many.",
      ),
  })
  .merge(dataSourceLinkSelectionSchema);

export type DataSourceAdapterBaseConfig = z.infer<
  typeof dataSourceAdapterBaseConfigSchema
>;
